import React, { useState } from 'react';
import clsx from 'clsx';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';

import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { detectSessionUserType, detectUserTypeFromIdentity } from '../../../utils/videoJoinToken';
import { markSessionEndConfirmed, shouldConfirmDoctorSessionEnd } from '../../../utils/sessionEnd';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      background: theme.brand,
      color: 'white',
      '&:hover': {
        background: '#600101',
      },
    },
  })
);

export default function EndCallButton(props: { className?: string }) {
  const classes = useStyles();
  const { room } = useVideoContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const identity = room?.localParticipant?.identity || '';
  const isDoctor = detectSessionUserType(identity) === 'doctor' || detectUserTypeFromIdentity(identity) === 'doctor';
  const needsConfirm = isDoctor && shouldConfirmDoctorSessionEnd();

  const disconnect = () => {
    if (room) {
      room.disconnect();
    }
  };

  const handleClick = () => {
    if (needsConfirm) {
      setConfirmOpen(true);
      return;
    }
    disconnect();
  };

  const handleContinueSession = () => {
    setConfirmOpen(false);
  };

  const handleEndAndGenerateReport = () => {
    markSessionEndConfirmed();
    setConfirmOpen(false);
    disconnect();
  };

  return (
    <>
      <Button onClick={handleClick} className={clsx(classes.button, props.className)} data-cy-disconnect>
        Disconnect
      </Button>
      <Dialog open={confirmOpen} onClose={handleContinueSession} maxWidth="sm" fullWidth>
        <DialogTitle>End session?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Do you want to end this visit and generate the medical report?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            If you disconnected by mistake, continue the session. Recording will keep running, and the report is
            generated only after you confirm the real end of the visit.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueSession} color="primary" data-cy-continue-session>
            Continue session
          </Button>
          <Button
            onClick={handleEndAndGenerateReport}
            className={classes.button}
            variant="contained"
            data-cy-end-and-generate
          >
            End session and generate report
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
